/**
 * The footer SEO text's HTML, as the hub reads it. Pure (no DOM): the server sanitises to the same allowlist the
 * editor's schema produces, so these helpers only look at text, never decide what is safe.
 */

/** The server's footer allowlist (`a` keeps only an http(s) or single-slash site `href`); the editor emits no other. */
export const FOOTER_ALLOWED_TAGS = ['h2', 'h3', 'p', 'strong', 'em', 'ul', 'ol', 'li', 'br', 'a'] as const;

const TAG_RE = /<[^>]*>/g;
const NBSP_RE = /&nbsp;|\u00a0/g;

const codePoint = (n: number): string => (n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : '');

/** The basic and numeric entities, `&amp;` last so `&amp;lt;` stays the text `&lt;` (innerHTML always escapes `&`). */
const decodeEntities = (text: string): string =>
  text
    .replace(NBSP_RE, ' ')
    .replace(/&#x([0-9a-f]+);/gi, (_, h: string) => codePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d: string) => codePoint(parseInt(d, 10)))
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&');

/** The visible text, each tag read as a word break and entities decoded (not trimmed, spaces not collapsed). */
export const footerPlainText = (html: string): string => decodeEntities(html.replace(TAG_RE, ' '));

/** Nothing to show: '', the editor's empty `<p></p>`, or markup holding only whitespace. */
export const isFooterHtmlEmpty = (html: string): boolean => footerPlainText(html).trim() === '';

export const footerWordCount = (html: string): number => {
  const text = footerPlainText(html).trim();
  return text === '' ? 0 : text.split(/\s+/).length;
};

/** The visible text of an element's inner HTML, whitespace runs collapsed and trimmed. */
const innerText = (inner: string): string => footerPlainText(inner).replace(/\s+/g, ' ').trim();

const PARAGRAPH_RE = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;
const HEADING_RE = /<h([23])\b[^>]*>([\s\S]*?)<\/h\1>/gi;

/** The text of the first `<p>` with visible text; '' when none. */
export const footerFirstParagraphText = (html: string): string => {
  for (const [, inner] of html.matchAll(PARAGRAPH_RE)) {
    const text = innerText(inner);
    if (text !== '') return text;
  }
  return '';
};

/** The text of every `<h2>` / `<h3>`, in document order (an empty heading gives ''). */
export const footerHeadingTexts = (html: string): string[] =>
  Array.from(html.matchAll(HEADING_RE), ([, , inner]) => innerText(inner));

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** One question and its answer: the question as an H3, the answer as the paragraph under it. */
export const faqBlockHtml = (question: string, answer: string): string =>
  `<h3>${escapeHtml(question)}</h3><p>${escapeHtml(answer)}</p>`;
